import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faSearch,
  faDownload,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";

interface MovementExcelRow {
  itemNumber: string;
  itemName: string;
  date: string;
  movementType: string;
  invoiceNumber: string;
  serial: string;
  warehouse: string;
  secondParty: string;
  price: string;
  notes: string;
  normalizedSerial: string;
  normalizedMovementType: string;
  sourceRow: number;
}

type ActiveTab = "clean" | "cancelled";

const MovementSerialFilter: React.FC = () => {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<MovementExcelRow[]>([]);
  const [duplicateSameMovementCount, setDuplicateSameMovementCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<ActiveTab>("clean");

  const normalizeDigits = (value: string) => {
    const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";

    return value.replace(/[٠-٩۰-۹]/g, (digit) => {
      const arabicIndex = arabicDigits.indexOf(digit);
      if (arabicIndex > -1) return String(arabicIndex);

      const persianIndex = persianDigits.indexOf(digit);
      if (persianIndex > -1) return String(persianIndex);

      return digit;
    });
  };

  const normalizeSerial = (value: any) => {
    return normalizeDigits(String(value ?? ""))
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, "")
      .replace(/[-–—]/g, "-")
      .trim()
      .toUpperCase();
  };

  const normalizeText = (value: any) => {
    return String(value ?? "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const normalizeMovement = (value: any) => {
    return normalizeText(value)
      .replace(/أ|إ|آ/g, "ا")
      .replace(/ة/g, "ه")
      .toLowerCase();
  };

  const normalizeHeader = (value: string) => {
    return String(value ?? "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, "")
      .replace(/_/g, "")
      .trim()
      .toLowerCase();
  };

  const findColumnKey = (row: any, aliases: string[]) => {
    const keys = Object.keys(row);
    const normalizedAliases = aliases.map(normalizeHeader);

    return keys.find((key) => {
      const normalizedKey = normalizeHeader(key);
      return normalizedAliases.includes(normalizedKey);
    });
  };

  const getValue = (row: any, aliases: string[]) => {
    const key = findColumnKey(row, aliases);
    return key ? row[key] : "";
  };

  const parseExcelFile = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json<any>(worksheet, {
      defval: "",
    });

    const parsedRows: MovementExcelRow[] = [];

    jsonData.forEach((row, index) => {
      const serial = normalizeText(
        getValue(row, [
          "الرقم التسلسلي",
          "السيريال",
          "سيريال",
          "Serial",
          "serial",
          "Serial Number",
          "serialNumber",
          "SN",
          "S/N",
        ])
      );

      const movementType = normalizeText(
        getValue(row, [
          "نوع الحركة",
          "الحركة",
          "Movement Type",
          "movementType",
          "Type",
          "type",
        ])
      );

      const normalizedSerial = normalizeSerial(serial);
      const normalizedMovementType = normalizeMovement(movementType);

      if (!normalizedSerial) return;

      parsedRows.push({
        itemNumber: normalizeText(
          getValue(row, ["رقم المادة", "Item Number", "itemNumber", "Item Code", "itemCode"])
        ),
        itemName: normalizeText(
          getValue(row, ["اسم المادة", "Item Name", "itemName", "Product Name", "productName"])
        ),
        date: normalizeText(getValue(row, ["التاريخ", "Date", "date"])),
        movementType,
        invoiceNumber: normalizeText(
          getValue(row, ["رقم الفاتورة", "Invoice Number", "invoiceNumber"])
        ),
        serial,
        warehouse: normalizeText(getValue(row, ["المستودع", "Warehouse", "warehouse"])),
        secondParty: normalizeText(
          getValue(row, ["الطرف الثاني", "Second Party", "secondParty"])
        ),
        price: normalizeText(getValue(row, ["السعر", "Price", "price"])),
        notes: normalizeText(
          getValue(row, ["ملاحظات الأقلام", "ملاحظات", "Notes", "notes"])
        ),
        normalizedSerial,
        normalizedMovementType,
        sourceRow: index + 2,
      });
    });

    return parsedRows;
  };

  const isSalesMovement = (movement: string) => {
    const value = normalizeMovement(movement);
    return value === "مبيعات";
  };

  const isSalesReturnMovement = (movement: string) => {
    const value = normalizeMovement(movement);
    return value === "مردودات مبيعات" || value === "مردودات المبيعات";
  };

  const filterRows = (inputRows: MovementExcelRow[]) => {
    const uniqueBySerialAndMovement = new Map<string, MovementExcelRow>();
    let duplicateCount = 0;

    inputRows.forEach((row) => {
      const key = `${row.normalizedSerial}__${row.normalizedMovementType}`;

      if (uniqueBySerialAndMovement.has(key)) {
        duplicateCount++;
        return;
      }

      uniqueBySerialAndMovement.set(key, row);
    });

    const uniqueRows = Array.from(uniqueBySerialAndMovement.values());

    const serialGroups = new Map<string, MovementExcelRow[]>();

    uniqueRows.forEach((row) => {
      const current = serialGroups.get(row.normalizedSerial) || [];
      current.push(row);
      serialGroups.set(row.normalizedSerial, current);
    });

    const cleanRows: MovementExcelRow[] = [];
    const cancelledRows: MovementExcelRow[] = [];

    Array.from(serialGroups.values()).forEach((group) => {
      const hasSales = group.some((row) => isSalesMovement(row.movementType));
      const hasReturn = group.some((row) => isSalesReturnMovement(row.movementType));

      if (hasSales && hasReturn) {
        cancelledRows.push(...group);
        return;
      }

      cleanRows.push(...group);
    });

    return {
      cleanRows,
      cancelledRows,
      duplicateCount,
    };
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      const parsedRows = await parseExcelFile(file);

      const { cleanRows, cancelledRows, duplicateCount } = filterRows(parsedRows);

      setRows(parsedRows);
      setDuplicateSameMovementCount(duplicateCount);
      setCancelledCount(cancelledRows.length);
      setFileName(file.name);

      toast.success(
        `تمت قراءة الملف: ${parsedRows.length} سطر، حذف ${duplicateCount} تكرار، وعزل ${cancelledRows.length} سطر بسبب مبيعات ومردودات`
      );

      event.target.value = "";
    } catch (error) {
      console.error("Excel parse error:", error);
      toast.error("حدث خطأ أثناء قراءة ملف الإكسل");
    }
  };

  const filteredData = useMemo(() => {
    return filterRows(rows);
  }, [rows]);

  const cleanRows = filteredData.cleanRows;
  const cancelledRows = filteredData.cancelledRows;

  const visibleCleanRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return cleanRows;

    return cleanRows.filter((row) => {
      return (
        row.itemNumber.toLowerCase().includes(q) ||
        row.itemName.toLowerCase().includes(q) ||
        row.movementType.toLowerCase().includes(q) ||
        row.invoiceNumber.toLowerCase().includes(q) ||
        row.serial.toLowerCase().includes(q) ||
        row.normalizedSerial.toLowerCase().includes(q) ||
        row.warehouse.toLowerCase().includes(q) ||
        row.secondParty.toLowerCase().includes(q)
      );
    });
  }, [cleanRows, searchQuery]);

  const visibleCancelledRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return cancelledRows;

    return cancelledRows.filter((row) => {
      return (
        row.itemNumber.toLowerCase().includes(q) ||
        row.itemName.toLowerCase().includes(q) ||
        row.movementType.toLowerCase().includes(q) ||
        row.invoiceNumber.toLowerCase().includes(q) ||
        row.serial.toLowerCase().includes(q) ||
        row.normalizedSerial.toLowerCase().includes(q) ||
        row.warehouse.toLowerCase().includes(q) ||
        row.secondParty.toLowerCase().includes(q)
      );
    });
  }, [cancelledRows, searchQuery]);

  const buildExportData = (exportRows: MovementExcelRow[]) => {
    return exportRows.map((row, index) => ({
      "#": index + 1,
      "رقم المادة": row.itemNumber,
      "اسم المادة": row.itemName,
      "التاريخ": row.date,
      "نوع الحركة": row.movementType,
      "رقم الفاتورة": row.invoiceNumber,
      "الرقم التسلسلي": row.serial,
      "Normalized Serial": row.normalizedSerial,
      "المستودع": row.warehouse,
      "الطرف الثاني": row.secondParty,
      "السعر": row.price,
      "ملاحظات الأقلام": row.notes,
      "رقم السطر في الملف": row.sourceRow,
    }));
  };

  const exportCleanRows = () => {
    if (cleanRows.length === 0) {
      toast.error("لا توجد بيانات نظيفة للتصدير");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(buildExportData(cleanRows));
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Clean Serials");
    XLSX.writeFile(workbook, "clean_movement_serials.xlsx");
  };

  const exportCancelledRows = () => {
    if (cancelledRows.length === 0) {
      toast.error("لا توجد بيانات محذوفة للتصدير");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(buildExportData(cancelledRows));
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Cancelled Serials");
    XLSX.writeFile(workbook, "cancelled_sales_returns_serials.xlsx");
  };

  const exportAllResults = () => {
    if (rows.length === 0) {
      toast.error("يرجى رفع ملف أولاً");
      return;
    }

    const workbook = XLSX.utils.book_new();

    const cleanSheet = XLSX.utils.json_to_sheet(buildExportData(cleanRows));
    const cancelledSheet = XLSX.utils.json_to_sheet(buildExportData(cancelledRows));

    XLSX.utils.book_append_sheet(workbook, cleanSheet, "Clean Serials");
    XLSX.utils.book_append_sheet(workbook, cancelledSheet, "Cancelled Serials");

    XLSX.writeFile(workbook, "movement_serials_filter_result.xlsx");
  };

  const handleReset = () => {
    setFileName("");
    setRows([]);
    setDuplicateSameMovementCount(0);
    setCancelledCount(0);
    setSearchQuery("");
    setActiveTab("clean");
  };

  const visibleRows = activeTab === "clean" ? visibleCleanRows : visibleCancelledRows;

  return (
    <div
      className="w-full max-w-none mx-auto bg-white rounded-xl shadow-lg"
      style={{ width: 1400, direction: "rtl" }}
    >
      <div className="max-w-full flex items-center justify-between bg-[#262d34] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl font-bold flex-1 text-right"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          فلترة سيريلات الحركات
        </h1>

        <div className="flex items-center gap-3">
          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleExcelUpload}
            />

            <Button
              variant="contained"
              component="span"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                backgroundColor: "#0f766e",
              }}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              رفع Excel
            </Button>
          </label>

          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              fontFamily: "Tajawal, sans-serif",
              color: "#fff",
              borderColor: "#fff",
            }}
            startIcon={<FontAwesomeIcon icon={faRotateRight} />}
          >
            إعادة تعيين
          </Button>
        </div>
      </div>

      <div
        style={{
          padding: "16px 24px",
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(180px, 1fr))",
          gap: 14,
          fontFamily: "Tajawal, sans-serif",
        }}
      >
        <StatCard
          title="إجمالي الأسطر المقروءة"
          value={rows.length}
          color="#2563eb"
          subTitle={fileName || "لم يتم اختيار ملف"}
        />

        <StatCard
          title="النتائج بعد التنظيف"
          value={cleanRows.length}
          color="#0f766e"
          subTitle="جاهزة للتصدير"
        />

        <StatCard
          title="تكرار بنفس الحركة"
          value={duplicateSameMovementCount}
          color="#ea580c"
          subTitle="تم تجاهله"
        />

        <StatCard
          title="مبيعات + مردودات"
          value={cancelledCount}
          color="#dc2626"
          subTitle="تم عزلها من النتائج"
        />

        <StatCard
          title="السيريالات المميزة"
          value={new Set(rows.map((x) => x.normalizedSerial)).size}
          color="#7c3aed"
          subTitle="بعد تنظيف السيريال"
        />
      </div>

      <div
        style={{
          padding: "0 24px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          fontFamily: "Tajawal, sans-serif",
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant={activeTab === "clean" ? "contained" : "outlined"}
            onClick={() => setActiveTab("clean")}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
          >
            البيانات بعد التنظيف
          </Button>

          <Button
            variant={activeTab === "cancelled" ? "contained" : "outlined"}
            color="error"
            onClick={() => setActiveTab("cancelled")}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
          >
            مبيعات + مردودات مبيعات
          </Button>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-80 shadow-md">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500 ml-2" />
            <input
              type="text"
              placeholder="البحث بالسيريال، المادة، الفاتورة، المستودع..."
              className="bg-transparent focus:outline-none w-full text-sm text-gray-700 py-1"
              style={{
                fontFamily: "Tajawal, sans-serif",
                textAlign: "right",
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Button
            variant="contained"
            color="success"
            onClick={exportCleanRows}
            disabled={cleanRows.length === 0}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            تصدير النظيف
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={exportCancelledRows}
            disabled={cancelledRows.length === 0}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            تصدير المعزول
          </Button>

          <Button
            variant="contained"
            onClick={exportAllResults}
            disabled={rows.length === 0}
            sx={{
              fontFamily: "Tajawal, sans-serif",
              backgroundColor: "#262d34",
            }}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            تصدير الكل
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          style={{
            padding: 30,
            textAlign: "center",
            fontFamily: "Tajawal, sans-serif",
            color: "#64748b",
            fontSize: 16,
          }}
        >
          يرجى رفع ملف Excel يحتوي على أعمدة الحركات والسيريالات.
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: "auto" }}>
          <table className="product-table" style={{ width: "100%" }}>
            <thead className="color-table">
              <tr>
                <th>#</th>
                <th>رقم المادة</th>
                <th>اسم المادة</th>
                <th>التاريخ</th>
                <th>نوع الحركة</th>
                <th>رقم الفاتورة</th>
                <th>الرقم التسلسلي</th>
                <th>المستودع</th>
                <th>الطرف الثاني</th>
                <th>السعر</th>
                <th>ملاحظات الأقلام</th>
                <th>صف الملف</th>
                <th>الحالة</th>
              </tr>
            </thead>

            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={`${row.normalizedSerial}-${row.normalizedMovementType}-${row.sourceRow}`}>
                  <td>{index + 1}</td>
                  <td>{row.itemNumber || "-"}</td>
                  <td>{row.itemName || "-"}</td>
                  <td>{row.date || "-"}</td>
                  <td>{row.movementType || "-"}</td>
                  <td>{row.invoiceNumber || "-"}</td>
                  <td style={{ direction: "ltr" }}>{row.serial || "-"}</td>
                  <td>{row.warehouse || "-"}</td>
                  <td>{row.secondParty || "-"}</td>
                  <td>{row.price || "-"}</td>
                  <td>{row.notes || "-"}</td>
                  <td>{row.sourceRow}</td>
                  <td>
                    {activeTab === "clean" ? (
                      <span
                        style={{
                          background: "#dcfce7",
                          color: "#166534",
                          padding: "5px 10px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        نظيف
                      </span>
                    ) : (
                      <span
                        style={{
                          background: "#fee2e2",
                          color: "#b91c1c",
                          padding: "5px 10px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        معزول
                      </span>
                    )}
                  </td>
                </tr>
              ))}

              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ textAlign: "center", padding: 20 }}>
                    لا توجد بيانات
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: number;
  color: string;
  subTitle?: string;
}> = ({ title, value, color, subTitle }) => {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "14px 18px",
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        minHeight: 105,
      }}
    >
      <div style={{ fontSize: 14, color: "#64748b", marginBottom: 6 }}>
        {title}
      </div>

      <div style={{ fontSize: 28, fontWeight: "bold", color }}>{value}</div>

      {subTitle && (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            marginTop: 5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={subTitle}
        >
          {subTitle}
        </div>
      )}
    </div>
  );
};

export default MovementSerialFilter;